// #64 ActionRow (DC-44): the Action Center row. The whole row is one <a> deep link to the exact screen: the kind icon,
// the verb — object (his §17 verbs), the requisition title with its mono id and the owner slot (HM-16 "Action · Owner ·
// Due": a pool's role word, or "for {name}"), the SLA clock (Due, still: nothing interactive nests in the link) and a
// chevron. An open row's clock carries the due phrase (Active Time) with the date after it, and its Calendar Time is
// read with the row's name ("SLA uses Active Time; Calendar Time remains visible."); a row with no due date carries the
// status word alone — the info *Due*, or its tier's word. An empty part draws no separator: no " — " without an
// object, no " · " unless the parts on both sides are there, no second line when none is. It lifts on hover; the
// focus ring composes with the lift shadow. `history` is the History tab's form: a Completed ok pill and "by {actor} ·
// {date/time}" in place of the clock.
// `closing`: the item was resolved; it fades and collapses in 200 ms, then onClosed runs (at once under reduced
// motion and capture; at the latest 400 ms on, should the collapse never fire transitionend). ActionRows is the list:
// skeleton rows while loading, *Nothing needs action* when empty. A row is an <li> only inside ActionRows; drawn alone
// it is a <div>. `flag`: a flag word drawn as a pill after the verb (DC-234, B13 F-143: *Urgent* on an urgent
// request's rows); none draws nothing.

import { Children, createContext, useContext, useEffect, useRef, type ReactNode } from "react";
import { CORE_LABELS, EmptyState, Icon, Pill, Skeleton } from "../core";
import { SLA_PILL_LABELS, SLA_TIER_LABELS } from "../labels";
import { cx } from "../lib/cx";
import { useHs, type Hs } from "../lib/hs";
import { useMc } from "../lib/McRoot";
import { formatDate, formatDateTime, type Instant } from "../lib/time";
import { Due, DUE_TONE, type DueTier } from "./Due";
import { METRIC_LABELS, METRIC_TEMPLATES } from "./labels";

export type ActionKind = "reqs" | "candidates" | "scheduling" | "packets" | "scorecards" | "panels" | "approvals" | "offers" | "closeout";

export interface ActionRowProps {
  kind: ActionKind;
  /** His verb (G §17). */
  verb: string;
  /** What it acts on: a candidate, a round, a package. */
  object: string;
  reqTitle: string;
  reqId: string;
  /** The deep link to the exact screen. */
  href: string;
  /** The SLA tier and the due instant (open rows). */
  tier?: DueTier | undefined;
  due?: Instant | undefined;
  /** The due phrase (Active Time) in the pill when the tier is "due", the date then following it; otherwise (or
   * without one) the clock carries the date. */
  phrase?: string | undefined;
  /** Calendar Time to the due date (e.g. "Calendar Time · 5 days"), read with the row's name. */
  calendar?: string | undefined;
  /** The owner slot: a pool's role word, or "for {name}" on a delegated row. */
  owner?: string | undefined;
  /** A flag word after the verb (DC-234: *Urgent* as a flag pill on an urgent request's rows). */
  flag?: string | undefined;
  /** The History tab's form. */
  history?: { actor: string; at: Instant } | undefined;
  closing?: boolean | undefined;
  onClosed?: (() => void) | undefined;
  can?: boolean | undefined;
  hs?: Hs | undefined;
  className?: string | undefined;
}

/** True inside ActionRows' <ul>. */
const InRows = createContext(false);

/** The status word of a row with no due date: the info *Due*, or its tier's word. */
const STATUS_WORD: Readonly<Record<DueTier, string>> = {
  due: SLA_TIER_LABELS.due,
  reminder: SLA_PILL_LABELS.reminder,
  nearBreach: SLA_PILL_LABELS.nearBreach,
  overdue: SLA_PILL_LABELS.overdue,
  pastDue: SLA_PILL_LABELS.pastDue,
};

export function ActionRow({
  kind, verb, object, reqTitle, reqId, href, tier = "due", due, phrase, calendar, owner, flag, history, closing, onClosed, can,
  hs, className,
}: ActionRowProps) {
  const hot = useHs(hs);
  const { reducedMotion } = useMc();
  const listed = useContext(InRows);
  const wrap = useRef<HTMLElement | null>(null);
  const closed = useRef(false);
  useEffect(() => {
    if (!closing || closed.current) return;
    const done = () => {
      if (closed.current) return;
      closed.current = true;
      onClosed?.();
    };
    if (reducedMotion) {
      done();
      return;
    }
    // transitionend is not promised: a collapse that never starts (closing set before the open row was first styled,
    // a hidden tab) fires none, so the row also leaves when the 200 ms collapse has had time to end.
    const element = wrap.current;
    const onEnd = (event: TransitionEvent) => { if (event.target === element) done(); };
    element?.addEventListener("transitionend", onEnd);
    const fallback = window.setTimeout(done, 400);
    return () => {
      element?.removeEventListener("transitionend", onEnd);
      window.clearTimeout(fallback);
    };
  }, [closing, onClosed, reducedMotion]);
  if (can === false) return null;
  const date = due !== undefined ? formatDate(due) : null;
  const Item = listed ? "li" : "div";
  return (
    <Item
      className={cx("mc-arow-w", closing && "mc-arow-closing")}
      ref={(element: HTMLElement | null) => { wrap.current = element; }}
      aria-hidden={closing ? true : undefined}
    >
      <a className={cx("mc-arow", history && "mc-arow-hist", className)} href={href} tabIndex={closing ? -1 : undefined} {...hot}>
        <span className="mc-arow-k" aria-hidden="true"><Icon name={`kind-${kind}`} size={20} /></span>
        <span className="mc-arow-m">
          <b className="mc-arow-v">{verb}{object ? <>{" — "}{object}</> : null}</b>
          {flag ? <Pill tone="flag" dot={false} className="mc-arow-flag">{flag}</Pill> : null}
          {reqTitle || reqId || owner ? (
            <span className="mc-arow-r">
              {reqTitle}{reqTitle && reqId ? " · " : null}{reqId ? <span className="mc-mono">{reqId}</span> : null}
              {(reqTitle || reqId) && owner ? " · " : null}{owner || null}
            </span>
          ) : null}
        </span>
        <span className="mc-arow-tail">
          {history ? (
            <>
              <Pill tone="ok">{METRIC_LABELS.completed}</Pill>
              <span className="mc-arow-d">{METRIC_TEMPLATES.completedBy({ actor: history.actor, at: formatDateTime(history.at) })}</span>
            </>
          ) : due !== undefined && date !== null ? (
            <>
              <Due tier={tier} at={due} phrase={tier === "due" ? phrase ?? date : date} still />
              {tier === "due" && phrase ? <span className="mc-arow-d">{date}</span> : null}
            </>
          ) : (
            <span className={cx("mc-due", `mc-due-${DUE_TONE[tier]}`)}>
              <Pill tone={DUE_TONE[tier]} dot={false}><Icon name="clock" size={14} />{STATUS_WORD[tier]}</Pill>
            </span>
          )}
          {calendar && !history ? <span className="mc-sr">{calendar}</span> : null}
          <Icon name="right" size={18} className="mc-arow-chev" />
        </span>
      </a>
    </Item>
  );
}

export interface ActionRowsProps {
  children?: ReactNode;
  loading?: boolean | number | undefined;
  /** The empty sentence (default *Nothing needs action*, pending Q-E15). */
  empty?: string | undefined;
  /** An id that names the list (a tab or a section title). */
  labelledBy?: string | undefined;
  className?: string | undefined;
}

export function ActionRows({ children, loading, empty = CORE_LABELS.nothingNeedsAction, labelledBy, className }: ActionRowsProps) {
  if (loading) {
    const count = typeof loading === "number" ? loading : 3;
    return (
      <div className={cx("mc-arows", className)} aria-busy="true">
        {Array.from({ length: count }, (_, index) => (
          <div key={index} className="mc-arow mc-arow-skel" aria-hidden="true">
            <Skeleton width={20} height={20} />
            <span className="mc-arow-m"><Skeleton width="70%" height={14} /><Skeleton width="45%" height={12} /></span>
            <Skeleton width={72} height={24} shape="pill" />
          </div>
        ))}
      </div>
    );
  }
  if (Children.toArray(children).length === 0) return <EmptyState className="mc-arows-empty">{empty}</EmptyState>;
  return <InRows.Provider value><ul className={cx("mc-arows", className)} aria-labelledby={labelledBy}>{children}</ul></InRows.Provider>;
}
